/**
 * The deck card: big and centered, like a swipe card.
 *
 *   ← left  Later (skip this sitting, never written)
 *   → right open the question (context, options, items)
 *   ↑ up    person tags
 */

import { useRef, useState } from "react";
import { personLabel, type Person } from "../lib/persons";
import type { Question } from "../lib/questions";
import { assessSwipe, dragFeedback } from "../lib/swipe";

export function CompactCard({
  q,
  persons,
  staged,
  onOpen,
  onSkip,
  onTag,
}: {
  q: Question;
  persons: Person[];
  staged?: boolean;
  onOpen: () => void;
  onSkip: () => void;
  onTag: () => void;
}) {
  const [drag, setDrag] = useState<{ dx: number; dy: number } | null>(null);
  const [fly, setFly] = useState<"left" | "right" | "up" | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const isConsolidation = q.kind === "consolidation";

  const down = (e: React.PointerEvent) => {
    start.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e: React.PointerEvent) => {
    if (!start.current) return;
    setDrag({ dx: e.clientX - start.current.x, dy: e.clientY - start.current.y });
  };
  const up = () => {
    const d = drag;
    start.current = null;
    if (!d) return;
    const dir = assessSwipe(d.dx, d.dy);
    if (!dir) {
      setDrag(null);
      return;
    }
    setFly(dir);
    window.setTimeout(() => {
      setFly(null);
      setDrag(null);
      if (dir === "left") onSkip();
      else if (dir === "right") onOpen();
      else onTag();
    }, 150);
  };

  const f = drag ? dragFeedback(drag.dx, drag.dy) : null;
  const style: React.CSSProperties | undefined = fly
    ? {
        transform:
          fly === "left"
            ? "translateX(-120%) rotate(-14deg)"
            : fly === "right"
              ? "translateX(120%) rotate(14deg)"
              : "translateY(-70%)",
        opacity: 0.15,
        transition: "all .15s ease",
      }
    : f
      ? { transform: `translate(${f.x}px, ${f.y}px) rotate(${f.rot}deg)` }
      : undefined;

  const hint =
    drag && drag.dx <= -60
      ? "later"
      : drag && drag.dx >= 60
        ? "open"
        : drag && drag.dy <= -60
          ? "tags"
          : null;

  return (
    <div className="relative select-none" style={{ touchAction: "none" }}>
      <div
        className={
          "card relative flex min-h-[46vh] flex-col p-5" +
          (isConsolidation ? " border-2 border-warn/40" : "")
        }
        style={{ ...style, cursor: "grab" }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onClick={() => {
          if (!drag || (Math.abs(drag.dx) < 8 && Math.abs(drag.dy) < 8)) onOpen();
        }}
      >
        <div className="flex flex-wrap items-center gap-1.5">
          {isConsolidation && (
            <span className="chip bg-warn/15 text-warn">Consolidation</span>
          )}
          <span className="chip bg-ink-850 text-ink-400">
            {q.domain}
            {q.area ? ` · ${q.area}` : ""}
          </span>
          {q.created && (
            <span className="chip bg-ink-850 text-ink-500">created {q.created}</span>
          )}
          {q.persons.map((p) => (
            <span key={p} className="chip bg-accent/10 text-accent-soft">
              {personLabel(persons, p) ?? p}
            </span>
          ))}
          {q.links.length > 0 && (
            <span className="chip bg-ink-850 text-ink-500">📄 {q.links.length}</span>
          )}
          {staged && <span className="chip bg-warn/15 text-warn">staged</span>}
        </div>

        <div className="flex flex-1 flex-col items-center justify-center py-4 text-center">
          <h2 className="text-2xl font-semibold leading-snug text-ink-100">{q.title}</h2>
          {q.context && (
            <p className="mt-3 line-clamp-4 text-sm leading-relaxed text-ink-400">
              {q.context}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between text-xs text-ink-500">
          <span>← later · → open · ↑ tags</span>
          {hint && <span className="font-medium text-accent-soft">{hint}</span>}
        </div>
      </div>
    </div>
  );
}
